// Export pleine résolution de ce qui est affiché : les couches du canevas (modalité active, surcouches, caches du
// recadrage et de la gomme) sont rejouées dans un canevas deck.gl hors écran, par carrés de CHUNK px, niveau par niveau
// (niveau k : zoom −k, comme à l'écran), puis découpées en tuiles JPEG d'un OME-TIFF pyramidal (tiffWriter.js).
import { Deck, OrthographicView } from '@deck.gl/core';

import { TiffPyramidWriter, jpegSubsampling, omeXml } from './tiffWriter';

const CHUNK = 2048;  // côté du canevas hors écran (px)
const TILE = 512;    // côté des tuiles du fichier
const LOAD_TIMEOUT = 120000;

// Niveaux de la pyramide : pleine résolution, puis divisée par 2 jusqu'à tenir dans 1024 px.
export function exportLevels(width, height) {
  const levels = [];
  for (let k = 0; ; k += 1) {
    const w = Math.ceil(width / 2 ** k);
    const h = Math.ceil(height / 2 ** k);
    levels.push({ k, width: w, height: h, across: Math.ceil(w / TILE), down: Math.ceil(h / TILE) });
    if (Math.max(w, h) <= 1024 || k >= 14) return levels;
  }
}

// layers : couches à rejouer (clonées) ; background : RGB du fond (hors image, transparence) ; region : { x0, y0, x1,
// y1 } en px pleine résolution ; sink : destination ; quality : JPEG (0 à 1) ; onProgress({ done, total }) ; signal :
// AbortSignal. Renvoie la taille du fichier.
export async function exportPyramid({
  layers, background, region, sink, quality = 0.9, name, pixelSizeUm, onProgress, signal,
}) {
  const width = Math.round(region.x1 - region.x0);
  const height = Math.round(region.y1 - region.y0);
  const levels = exportLevels(width, height);
  const total = levels.reduce((a, l) => a + l.across * l.down, 0);
  const writer = new TiffPyramidWriter(sink);
  const bg = `rgb(${background.join(',')})`;

  // Canevas hors écran (hors de la vue, dimensions fixes, 1 px de canevas = 1 px de tuile).
  const parent = document.createElement('div');
  parent.style.cssText = `position:fixed;left:-${CHUNK + 1000}px;top:0;width:${CHUNK}px;height:${CHUNK}px;pointer-events:none;`;
  document.body.appendChild(parent);
  const capture = document.createElement('canvas');
  capture.width = CHUNK;
  capture.height = CHUNK;
  const captureCtx = capture.getContext('2d', { willReadFrequently: true });
  const tileCanvas = document.createElement('canvas');
  tileCanvas.width = TILE;
  tileCanvas.height = TILE;
  const tileCtx = tileCanvas.getContext('2d', { willReadFrequently: true });

  let waiting = null;  // { resolve, renders } : capture au prochain rendu complet
  const clones = layers.map(l => l.clone({}));
  const deck = new Deck({
    parent, width: CHUNK, height: CHUNK, useDevicePixels: false, controller: false,
    views: [new OrthographicView({ id: 'export' })],
    viewState: { target: [0, 0, 0], zoom: 0 },
    layers: [],  // passées avec la vue (render) : celles du constructeur ne sont pas reprises une fois WebGL prêt
    onAfterRender: () => {
      if (!waiting) return;
      // Complet : couches créées et chargées (tuiles de la vue comprises), après au moins un rendu de la nouvelle vue
      // (le premier choisit les tuiles).
      waiting.renders += 1;
      const all = deck.layerManager?.getLayers() || [];
      if (waiting.renders < 2 || !all.length || !all.every(l => l.isLoaded)) return;
      // Copie pendant le rendu (le tampon WebGL n'est pas conservé ensuite).
      captureCtx.fillStyle = bg;
      captureCtx.fillRect(0, 0, CHUNK, CHUNK);
      captureCtx.drawImage(deck.getCanvas ? deck.getCanvas() : deck.canvas, 0, 0);
      const { resolve } = waiting;
      waiting = null;
      resolve();
    },
    onError: err => console.error('Export (deck.gl) :', err),
  });
  const render = (target, zoom) => new Promise((resolve, reject) => {
    waiting = { resolve, renders: 0 };
    deck.setProps({ layers: clones, viewState: { target: [...target, 0], zoom } });
    const started = Date.now();
    const poll = () => {
      if (!waiting) return;
      if (signal?.aborted) { waiting = null; reject(new DOMException('Export stopped', 'AbortError')); return; }
      if (Date.now() - started > LOAD_TIMEOUT) { waiting = null; reject(new Error('tiles took too long to load')); return; }
      // WebGL prêt après la création (asynchrone) : couches données avant perdues, à redonner.
      if (deck.layerManager && !deck.layerManager.getLayers().length) {
        deck.setProps({ layers: clones, viewState: { target: [...target, 0], zoom } });
      }
      // Couches mises à jour ici (tuiles arrivées…) plutôt que par la boucle d'animation de deck.gl, suspendue quand
      // l'onglet n'est pas au premier plan.
      deck.layerManager?.updateLayers();
      deck.redraw('export');
      setTimeout(poll, 100);
    };
    setTimeout(poll, 50);
  });

  let done = 0;
  try {
    const written = [];
    for (const level of levels) {
      const scale = 2 ** level.k;  // px pleine résolution par px du niveau
      const tiles = new Array(level.across * level.down);
      const chunksX = Math.ceil(level.width / CHUNK);
      const chunksY = Math.ceil(level.height / CHUNK);
      for (let cy = 0; cy < chunksY; cy += 1) {
        for (let cx = 0; cx < chunksX; cx += 1) {
          // Centre du carré, en px pleine résolution : son coin haut gauche tombe sur le pixel (cx, cy) × CHUNK.
          const target = [region.x0 + (cx * CHUNK + CHUNK / 2) * scale, region.y0 + (cy * CHUNK + CHUNK / 2) * scale];
          await render(target, -level.k);
          for (let ty = 0; ty < CHUNK / TILE; ty += 1) {
            for (let tx = 0; tx < CHUNK / TILE; tx += 1) {
              const gx = cx * (CHUNK / TILE) + tx;
              const gy = cy * (CHUNK / TILE) + ty;
              if (gx >= level.across || gy >= level.down) continue;
              tileCtx.fillStyle = bg;
              tileCtx.fillRect(0, 0, TILE, TILE);
              // Hors du niveau (tuiles du bord) : fond.
              const w = Math.min(TILE, level.width - gx * TILE);
              const h = Math.min(TILE, level.height - gy * TILE);
              tileCtx.drawImage(capture, tx * TILE, ty * TILE, w, h, 0, 0, w, h);
              const px = tileCtx.getImageData(0, 0, TILE, TILE).data;
              let uniform = true;
              for (let i = 4; i < px.length; i += 4) {
                if (px[i] !== px[0] || px[i + 1] !== px[1] || px[i + 2] !== px[2]) { uniform = false; break; }
              }
              const key = uniform ? `${px[0]},${px[1]},${px[2]}` : null;
              let bytes = null;
              if (!uniform || !writer.shared.has(key)) {
                const blob = await new Promise(res => tileCanvas.toBlob(res, 'image/jpeg', quality));
                bytes = new Uint8Array(await blob.arrayBuffer());
                if (!writer.subsampling) writer.subsampling = jpegSubsampling(bytes);  // celui du navigateur
              }
              tiles[gy * level.across + gx] = await writer.writeTile(bytes, key);
              done += 1;
            }
          }
          onProgress?.({ done, total });
        }
      }
      written.push({ width: level.width, height: level.height, tileSize: TILE, tiles });
    }
    written.forEach(l => { l.subsampling = writer.subsampling || [2, 2]; });
    return await writer.finish(written, omeXml({ name, width, height, pixelSizeUm }));
  } catch (err) {
    await sink.abort?.();
    throw err;
  } finally {
    deck.finalize();
    parent.remove();
  }
}
